package com.taocent.simple.compose.component.blockrichtext

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.TextUnit
import com.taocent.simple.compose.component.richtext.core.RichTextState
import com.taocent.simple.compose.component.richtext.core.internal.format.RichTextFormatState
import com.taocent.simple.compose.component.blockrichtext.internal.registry.BlockRegistry
import com.taocent.simple.compose.component.blockrichtext.internal.serialization.TextBlockSerializer
import com.taocent.simple.compose.component.blockrichtext.internal.serialization.TableBlockSerializer
import com.taocent.simple.compose.component.blockrichtext.internal.block.AnchorPosition
import com.taocent.simple.compose.component.blockrichtext.internal.block.EditorCursor
import com.taocent.simple.compose.component.blockrichtext.internal.undo.BlockUndoManager

/**
 * 块级富文本状态容器。
 *
 * 字段与核心方法全部留在主类;按领域拆分的业务方法以 `internal` 扩展函数形式
 * 分布在同包下的 `BlockState*.kt` 文件中:
 * - [BlockStateFocus] — 文本块/单元格焦点、锚点光标、焦点恢复
 * - [BlockStateDocument] — undo/redo、文档快照、文本块内容同步
 * - [BlockStateInsert] — 块插入、表格插入
 * - [BlockStateTableOps] — 表格行列增删改、单元格内容、整体删除
 * - [BlockStateMergeDelete] — 块合并、删除、左高亮 Backspace 拦截
 * - [BlockStateNavigation] — 表格上下/插入/删除键盘导航
 * - [BlockStateJson] — Block JSON 序列化与粘贴识别
 */
@ExperimentalBlockRichTextApi
class BlockState(
    maxUndoHistory: Int = 100,
    undoMergeIntervalMs: Long = 500
) : RichTextFormatState {

    // region 字段(同包扩展函数访问: 公共 var 的 set 提升为 internal,private val/var 提升为 internal)

    internal val _blocks = mutableStateListOf<DocumentBlock>()
    val blocks: List<DocumentBlock> get() = _blocks

    internal val idToIndex = mutableMapOf<String, Int>()

    /** O(1) 块索引查找，替代 `_blocks.indexOfFirst { it.id == blockId }`。索引缺失时回退到线性查找。 */
    fun findBlockIndex(blockId: String): Int {
        val cached = idToIndex[blockId]
        if (cached != null && cached < _blocks.size && _blocks[cached].id == blockId) {
            return cached
        }
        val linear = _blocks.indexOfFirst { it.id == blockId }
        if (linear >= 0) idToIndex[blockId] = linear
        return linear
    }

    /** 全量重建索引（用于 _blocks.clear() 后重建） */
    internal fun rebuildIdToIndex() {
        idToIndex.clear()
        _blocks.forEachIndexed { index, block -> idToIndex[block.id] = index }
    }

    /** 在 index 处插入块时更新索引 */
    internal fun onBlockInserted(index: Int, block: DocumentBlock) {
        idToIndex[block.id] = index
        for (i in index + 1 until _blocks.size) {
            idToIndex[_blocks[i].id] = i
        }
    }

    /** 删除 index 处的块时更新索引 */
    internal fun onBlockRemoved(index: Int) {
        val removedId = _blocks.getOrNull(index)?.id
        if (removedId != null) idToIndex.remove(removedId)
        for (i in index until _blocks.size) {
            idToIndex[_blocks[i].id] = i
        }
    }

    /** 替换 index 处的块时更新索引（ID 不变则无需更新） */
    internal fun onBlockReplaced(index: Int, oldId: String, newBlock: DocumentBlock) {
        if (oldId != newBlock.id) {
            idToIndex.remove(oldId)
            idToIndex[newBlock.id] = index
        }
    }

    var focusedBlockId by mutableStateOf("")
        internal set

    /**
     * Web 端 Cmd/Ctrl+V 双路径修复的 BlockState 级别 flag。
     *
     * [BlockRichTextEditor] 顶层 onPreviewKeyEvent 在主动调
     * `clipboardActions.paste()`(还原 spans + emoji)之前,会同步设本 flag 为 true。
     * web 端 [com.taocent.simple.compose.component.blockrichtext.internal.platform.InterceptImePasteTextInput]
     * 注册的 `paste` event capture listener 看到本 flag 为 true 时调
     * `stopImmediatePropagation()`,**阻止**原生 `paste` 事件继续派发到
     * `BasicTextField`(contentEditable)内部,避免 `BasicTextField` 走默认 IME
     * 路径插入 plain text。
     *
     * 与 `RichTextState.suppressNextImePasteOnValueChange`(iOS / 旧 Android
     * 通过 `onValueChange` 拦截)互为冗余:本 flag 走 DOM 级别阻止,适用于
     * web 端 `BasicTextField` 内部 native paste 路径不调 `onValueChange` 回调
     * 直接通过 `PlatformTextInput` 更新 state 的场景。
     */
    internal var suppressNextNativePasteEvent by mutableStateOf(false)

    internal var editorCursor: EditorCursor by mutableStateOf(EditorCursor.Text(""))
        internal set

    var selectedTableBlockId by mutableStateOf<String?>(null)
        internal set

    var navSelectedTableId by mutableStateOf<String?>(null)
        internal set
    var navSourceBlockId by mutableStateOf<String?>(null)
        internal set
    var pendingNavRestoreTableId by mutableStateOf<String?>(null)

    var navRowAnchorTableId by mutableStateOf<String?>(null)
        internal set
    var navRowAnchorRowId by mutableStateOf<String?>(null)
        internal set
    var navRowDeleteGuardRowId by mutableStateOf<String?>(null)
        internal set
    var navRowDeleteGuardTime by mutableStateOf<Long?>(null)
        internal set
    var focusRequestVersion by mutableStateOf(0)
        internal set
    var contentSyncVersion by mutableStateOf(0)
        internal set
    val navFocusRequesters = mutableMapOf<String, androidx.compose.ui.focus.FocusRequester>()

    internal val blockStates = mutableMapOf<String, RichTextState>()
    internal val blockUndoManager = BlockUndoManager(maxUndoHistory, undoMergeIntervalMs)
    internal var suppressContentUndoRecord = false
    internal var pendingHyperlinkDeleteSnapshot: BlockUndoManager.DocumentSnapshot? = null
    internal var pendingHyperlinkDeleteKey: String? = null

    var focusedTableCellState: RichTextState? by mutableStateOf(null)
        internal set

    var focusedTableCellFocusRequester: androidx.compose.ui.focus.FocusRequester? by mutableStateOf(null)
        internal set

    /**
     * 跨 block 进入表格单元格的请求载荷。
     *
     * TextBlock ↑↓ 在块首/块末进入相邻表格时,由 [BlockStateNavigation.requestEnterTableCell]
     * 写入,值形如 `(tableId, rowId, cellId, caretAtEnd)`。
     * [com.taocent.simple.compose.component.blockrichtext.internal.block.renderer.TableRenderer]
     * 监听同 `tableId` 的请求,首次匹配后调 `focusCellAtCaret(...)` 触发 FocusRequester,
     * 之后立即清空字段(消费一次)。
     *
     * 写入此字段不会触发整文档重组(`mutableStateOf`),只有订阅它的 TableRenderer
     * 会在下一帧响应。
     */
    internal var pendingEnterTableCell: PendingEnterTableCell? by mutableStateOf(null)
    /**
     * 触发表格容器自动滚动的请求载荷(行级/表格级锚点高亮时写入)。
     *
     * 背景:列数超过 [com.taocent.simple.compose.component.blockrichtext.TableConfig.maxColumnsBeforeScroll]
     * 时表格水平可滚动,用户自己滑动后锚点高亮线可能被遮挡。该请求让 TableRenderer
     * 在下一帧把容器滚到首列/末列以保证锚点可见。一次性的,处理后立刻清空。
     */
    internal var pendingTableScrollRequest: PendingTableScrollRequest? by mutableStateOf(null)

    /**
     * 用户是否曾经**主动**让任何 RichTextTextField(文本块或表格单元格)实际获得过焦点。
     *
     * 与 [editorCursor] 的区别:`editorCursor` 在 `init` 时就会被设为 `Text(firstBlock.id, ...)`,
     * 并不能区分"还没人点过"和"已经在 first block 内部"。本标志只在 onFocusChanged(isFocused=true)
     * 路径下被设为 true,可用于:
     * - emoji 插入后:仅当本标志为 true 才 `requestFocus()`,避免无焦点时拉起软键盘
     *   (Android/iOS 移动端、mobile webview 都会因 requestFocus 弹软键盘)
     * - hyperlink / 表格插入 / 导出 dialog 关闭后:仅当本标志为 true 才恢复焦点,
     *   解决「软键盘本就未展开时 dialog 关闭后焦点丢失」(原条件只看键盘可见性)
     */
    var hasUserFocusedAnyField: Boolean by mutableStateOf(false)
        internal set

    /**
     * Backspace 长按保护：当一次长按中已删除一个非文本块后置为 true，
     * 阻止 key repeat 继续级联删除后续块。标志在 Backspace KeyUp 时清除。
     */
    var backspaceBlockDeleteGuard by mutableStateOf(false)
        internal set

    /**
     * guard 冷却版本号：每次 SET 或 onUserValueChange 回退（bump）时自增 1。
     * LaunchedEffect 重启 300ms delay，长按期间不休眠，松手后自动清除。
     */
    internal var guardCooldownVersion by mutableStateOf(0)
        private set

    internal fun bumpGuardCooldown() {
        guardCooldownVersion++
    }

    val canUndo: Boolean
        get() = blockUndoManager.canUndo

    val canRedo: Boolean
        get() = blockUndoManager.canRedo

    init {
        val firstBlock = TextBlock()
        _blocks.add(firstBlock)
        idToIndex[firstBlock.id] = 0
        blockStates[firstBlock.id] = createBlockState()
        setTextCursor(firstBlock.id)
    }

    // endregion

    // region 核心公共 API

    internal fun createBlockState(initialText: String = ""): RichTextState {
        return RichTextState(
            initialText = initialText,
            maxUndoHistory = 0,
            undoMergeIntervalMs = 0
        )
    }

    internal fun createBlockState(content: AnnotatedString): RichTextState {
        return createBlockState().also {
            it.restoreTextFieldValue(
                TextFieldValue(
                    annotatedString = content,
                    selection = TextRange(content.length)
                )
            )
        }
    }

    fun getBlockState(blockId: String): RichTextState {
        return blockStates[blockId] ?: run {
            val block = _blocks.firstOrNull { it.id == blockId }
            val state = if (block is TextBlock) {
                createBlockState(block.content)
            } else {
                createBlockState()
            }
            blockStates[blockId] = state
            state
        }
    }

    /**
     * 获取 [TextBlock] 的当前有效内容，优先从 [blockStates] 读取，
     * 降级回退到 [TextBlock.content]（初始值/序列化缓存）。
     *
     * 这是 [TextBlock] 内容的唯一读取入口，消除双源真相问题。
     */
    fun getEffectiveContent(block: TextBlock): AnnotatedString {
        return blockStates[block.id]?.textFieldValue?.annotatedString ?: block.content
    }

    val focusedBlockState: RichTextState
        get() = getBlockState(focusedBlockId)

    private val activeRichTextState: RichTextState
        get() = focusedTableCellState ?: focusedBlockState

    fun focusBlock(blockId: String) {
        clearTableCellFocus()
        setTextCursor(blockId)
    }

    fun focusTableCell(
        tableId: String,
        rowId: String,
        cellId: String,
        cellState: RichTextState,
        focusRequester: androidx.compose.ui.focus.FocusRequester
    ) {
        setTableCellCursor(tableId, rowId, cellId, cellState, focusRequester)
    }

    fun focusTableCell(cellState: RichTextState, focusRequester: androidx.compose.ui.focus.FocusRequester) {
        setLegacyTableCellFocus(cellState, focusRequester)
    }

    fun requestFocusedBlockFocus() {
        focusRequestVersion++
    }

    // endregion

    // region JSON 输出

    private val blockRegistry: BlockRegistry by lazy {
        BlockRegistry().apply {
            registerSerializer(TextBlockSerializer())
            registerSerializer(TableBlockSerializer())
        }
    }

    override fun toJson(): String {
        val parts = _blocks.map { block ->
            blockRegistry.toJson(block) { b ->
                blockStates[b.id]?.textFieldValue?.annotatedString
            } ?: ""
        }
        return "{\"blocks\":[$parts]}"
    }

    // endregion

    // region RichTextFormatState - proxy to active state (text block or table cell)

    override val currentFontSize: TextUnit
        get() = activeRichTextState.currentFontSize
    override val currentColor: Color
        get() = activeRichTextState.currentColor
    override val currentBackground: Color
        get() = activeRichTextState.currentBackground
    override val currentBold: Boolean
        get() = activeRichTextState.currentBold
    override val currentItalic: Boolean
        get() = activeRichTextState.currentItalic
    override val currentUnderline: Boolean
        get() = activeRichTextState.currentUnderline
    override val currentStrikethrough: Boolean
        get() = activeRichTextState.currentStrikethrough
    override val currentSuperscript: Boolean
        get() = activeRichTextState.currentSuperscript
    override val currentSubscript: Boolean
        get() = activeRichTextState.currentSubscript
    override val currentTextAlign: TextAlign
        get() = activeRichTextState.currentTextAlign

    override fun currentSpanStyle(): SpanStyle = activeRichTextState.currentSpanStyle()

    override fun toggleBold() = activeRichTextState.toggleBold()
    override fun toggleItalic() = activeRichTextState.toggleItalic()
    override fun toggleUnderline() = activeRichTextState.toggleUnderline()
    override fun toggleStrikethrough() = activeRichTextState.toggleStrikethrough()
    override fun toggleSuperscript() = activeRichTextState.toggleSuperscript()
    override fun toggleSubscript() = activeRichTextState.toggleSubscript()
    override fun clearFormatting() = activeRichTextState.clearFormatting()
    override fun setFontSize(size: TextUnit) = activeRichTextState.setFontSize(size)
    override fun setColor(color: Color) = activeRichTextState.setColor(color)
    override fun setBackground(color: Color) = activeRichTextState.setBackground(color)
    override fun setTextAlign(align: TextAlign) = activeRichTextState.setTextAlign(align)

    override fun saveSelection() = activeRichTextState.saveSelection()
    override fun restoreSavedSelection() = activeRichTextState.restoreSavedSelection()

    override fun undo() {
        pendingHyperlinkDeleteSnapshot = null
        pendingHyperlinkDeleteKey = null
        when (val result = blockUndoManager.undo() ?: return) {
            is BlockUndoManager.UndoResult.Block -> withContentUndoSuppressed {
                val snapshot = result.snapshot
                val state = getBlockState(snapshot.blockId)
                state.restoreTextFieldValue(snapshot.value)
                setFocusedBlockCompat(snapshot.blockId)
                updateBlockContent(snapshot.blockId)
            }
            is BlockUndoManager.UndoResult.Document -> restoreDocumentSnapshot(result.snapshot)
        }
    }

    override fun redo() {
        pendingHyperlinkDeleteSnapshot = null
        pendingHyperlinkDeleteKey = null
        when (val result = blockUndoManager.redo() ?: return) {
            is BlockUndoManager.UndoResult.Block -> withContentUndoSuppressed {
                val snapshot = result.snapshot
                val state = getBlockState(snapshot.blockId)
                state.restoreTextFieldValue(snapshot.value)
                setFocusedBlockCompat(snapshot.blockId)
                updateBlockContent(snapshot.blockId)
            }
            is BlockUndoManager.UndoResult.Document -> restoreDocumentSnapshot(result.snapshot)
        }
    }

    override fun insertText(text: String) = activeRichTextState.insertText(text)
    override fun insertCustomEmoji(emojiId: String, displaySize: TextUnit) =
        activeRichTextState.insertCustomEmoji(emojiId, displaySize)
    override fun insertHyperlink(text: String, url: String) =
        activeRichTextState.insertHyperlink(text, url)

    // endregion
}
